import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { showSuccess, showError, showWarning } from '../utils/toast';

const WelcomeModal = ({ isOpen, onClose }) => {
  const { i18n } = useTranslation();
  const [step, setStep] = useState(1);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const modalRef = useRef(null);
  const [permissions, setPermissions] = useState({
    notifications: null,
    location: null
  });
  const [currentLang, setCurrentLang] = useState(i18n.language || 'en');

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent closing modal by clicking outside or ESC key - users must complete onboarding
  // This ensures first-time visitors go through the entire welcome flow
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  // Auto-close after 3 seconds on the welcome step (now step 2)
  useEffect(() => {
    if (isOpen && step === 2) {
      const timer = setTimeout(() => {
        setStep(3);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isOpen, step]);

  // Handle language change
  const handleLanguageChange = async (newLang) => {
    try {
      await i18n.changeLanguage(newLang);
      setCurrentLang(newLang);
      localStorage.setItem('i18nextLng', newLang);
      showSuccess(`🌐 Language changed to ${newLang.toUpperCase()}`);
    } catch (error) {
      showError('❌ Could not change language.');
    }
  };

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      showError('❌ This browser does not support notifications.');
      return;
    }

    if (Notification.permission === 'granted') {
      setPermissions(prev => ({ ...prev, notifications: 'granted' }));
      showSuccess('🔔 Notifications already enabled!');
      return;
    }

    if (Notification.permission === 'denied') {
      setPermissions(prev => ({ ...prev, notifications: 'denied' }));
      showWarning('⚠️ Notifications blocked. Please enable in browser settings.');
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissions(prev => ({ ...prev, notifications: permission }));
      
      if (permission === 'granted') {
        showSuccess('🔔 Notifications enabled! We\'ll keep you updated.');
        setTimeout(() => {
          new Notification('MaxiCar', {
            body: 'Welcome! You\'ll receive ride updates here.',
            icon: '/icons8-taxi-ios_filled-16.png'
          });
        }, 500);
      } else {
        showWarning('⚠️ Notifications disabled. You can enable them later in settings.');
      }
    } catch (error) {
      showError('❌ Could not request notification permission.');
    }
  };

  const requestLocationPermission = async () => {
    if (!('geolocation' in navigator)) {
      showError('❌ Geolocation is not supported by this browser.');
      setPermissions(prev => ({ ...prev, location: 'denied' }));
      return;
    }

    try {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setPermissions(prev => ({ ...prev, location: 'granted' }));
          showSuccess('📍 Location access granted! We can help you faster.');
          localStorage.setItem('userLocation', JSON.stringify({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
            timestamp: Date.now()
          }));
        },
        (error) => {
          setPermissions(prev => ({ ...prev, location: 'denied' }));
          
          if (error.code === error.PERMISSION_DENIED) {
            showWarning('⚠️ Location access denied. You can enable it later.');
          } else if (error.code === error.POSITION_UNAVAILABLE) {
            showError('❌ Location information unavailable.');
          } else if (error.code === error.TIMEOUT) {
            showError('❌ Location request timed out.');
          } else {
            showError('❌ Could not get your location.');
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 300000
        }
      );
    } catch (error) {
      showError('❌ Could not request location permission.');
      setPermissions(prev => ({ ...prev, location: 'denied' }));
    }
  };

  const handleFinish = () => {
    localStorage.setItem('maxicar_onboarding_complete', 'true');
    localStorage.setItem('maxicar_permissions', JSON.stringify(permissions));
    onClose();
  };

  if (!isOpen) return null;

  // Modal styles based on device
  const modalStyle = isMobile ? {
    position: 'fixed',
    bottom: 0,
    left: 0,
    right: 0,
    margin: 0,
    borderTopLeftRadius: '1.5rem',
    borderTopRightRadius: '1.5rem',
    maxHeight: '90vh',
    overflowY: 'auto',
    width: '100vw',
    maxWidth: '100%',
    transform: 'translateY(0)',
    transition: 'transform 0.3s ease-out',
    padding: '1.5rem',
    boxSizing: 'border-box',
    overflowX: 'hidden'
  } : {
    width: '100%',
    maxWidth: '480px',
    margin: '1rem auto',
    borderRadius: '1.5rem',
    padding: '2rem',
    boxSizing: 'border-box'
  };

  const containerStyle = isMobile ? {
    alignItems: 'flex-end',
    padding: 0,
    margin: 0,
    maxWidth: '100%',
    width: '100%',
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center'
  } : {
    maxWidth: '100%',
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '1rem'
  };

  return (
    <div className="fixed inset-0 overflow-y-auto" style={{ 
      zIndex: 9999,
      width: '100%',
      maxWidth: '100vw',
      margin: 0,
      padding: 0 
    }}>
      <div 
        className="flex items-center justify-center min-h-screen p-0 text-center"
        style={containerStyle}
      >
        <div 
          className="fixed inset-0 bg-black bg-opacity-70 transition-opacity"
          style={{ 
            zIndex: -1,
            width: '100vw',
            height: '100vh',
            margin: 0,
            padding: 0 
          }}
        ></div>

        <div 
          ref={modalRef}
          style={{
            ...modalStyle,
            backgroundColor: 'white',
            textAlign: 'center',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            position: 'relative',
            border: '2px solid #f59e0b',
          }}
          className="transform transition-all"
        >
          {isMobile && step !== 1 && (
            <div className="flex justify-center mb-4">
              <div className="w-12 h-1.5 bg-gray-300 rounded-full"></div>
            </div>
          )}
          {step === 1 && (
            <div>
              <div className="text-5xl mb-5">🌐</div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 bg-gradient-to-r from-blue-500 to-blue-600 bg-clip-text text-transparent">
                Choose Your Language
              </h2>
              <p className="text-gray-600 mb-4 text-base leading-relaxed">
                Select your preferred language to get started with MaxiCar.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-6">
                <p className="text-blue-700 text-sm font-medium">
                  ✨ Required setup for first-time visitors
                </p>
              </div>
              <div className="space-y-3">
                {[
                  { code: 'en', name: 'English', nativeName: 'English', flag: '🇺🇸' },
                  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪' },
                  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' }
                ].map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      handleLanguageChange(language.code);
                      setTimeout(() => setStep(2), 500);
                    }}
                    className={`w-full flex items-center p-3 rounded-lg transition-all duration-200 text-left ${
                      currentLang === language.code
                        ? 'bg-blue-50 border-2 border-blue-400 text-blue-800'
                        : 'border-2 border-gray-200 hover:border-blue-300 hover:bg-blue-50 text-gray-700'
                    }`}
                  >
                    <span className="text-2xl mr-3">{language.flag}</span>
                    <div className="flex-1">
                      <div className="font-semibold">{language.nativeName}</div>
                      <div className="text-sm opacity-70">{language.name}</div>
                    </div>
                    {currentLang === language.code && (
                      <div className="flex items-center justify-center w-5 h-5 bg-blue-400 rounded-full ml-2">
                        <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="text-5xl mb-5">🚕</div>
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                Welcome to MaxiCar!
              </h2>
              <p className="text-gray-600 mb-6 text-base leading-relaxed">
                Your premium taxi service in Worms & surroundings. Let's set up your experience for the best service.
              </p>
              <button
                onClick={() => setStep(3)}
                className="w-full py-4 px-6 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
              >
                Get Started 🚀
              </button>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="text-5xl mb-5">🔔</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Stay Updated
              </h3>
              <p className="text-gray-600 mb-8 text-base leading-relaxed">
                Allow MaxiCar to send you notifications about your rides, driver updates, and important information.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => {
                    requestNotificationPermission();
                    setTimeout(() => setStep(4), 1000);
                  }}
                  className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-green-500 to-green-600 text-white font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors duration-200"
                >
                  Enable Notifications
                </button>
                <button
                  onClick={() => setStep(4)}
                  className="w-full py-3 px-6 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <div className="text-5xl mb-5">📍</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                Enable Location
              </h3>
              <p className="text-gray-600 mb-8 text-base leading-relaxed">
                Allow MaxiCar to access your location for faster pickups and accurate ride tracking.
              </p>
              <div className="space-y-4">
                <button
                  onClick={() => {
                    requestLocationPermission();
                    setTimeout(() => setStep(5), 1000);
                  }}
                  className="w-full py-3 px-6 rounded-lg bg-gradient-to-r from-blue-500 to-blue-600 text-white font-medium hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                >
                  Enable Location
                </button>
                <button
                  onClick={() => setStep(5)}
                  className="w-full py-3 px-6 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
                >
                  Skip for now
                </button>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <div className="text-6xl mb-6">🎉</div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">
                You're All Set!
              </h3>
              <p className="text-gray-600 mb-8 text-base leading-relaxed">
                Welcome to MaxiCar! You can now book rides, track your trips, and enjoy our premium service.
              </p>
              <button
                onClick={handleFinish}
                className="w-full py-4 px-6 rounded-lg bg-gradient-to-r from-yellow-500 to-yellow-600 text-white font-bold hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 transition-colors duration-200"
              >
                Start Using MaxiCar 🚕
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WelcomeModal;